import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getPhasePhotos } from '../../services/photoService';
import { getCase, getCasePhases } from '../../services/caseService';
import { getPatient } from '../../services/patientService';
import { getAllCategories } from '../../constants/dawsonCategories';

const PhotoComparison = () => {
  const { patientId, caseId } = useParams();
  const { userProfile } = useAuth();

  const [patient, setPatient] = useState(null);
  const [treatmentCase, setTreatmentCase] = useState(null);
  const [phases, setPhases] = useState([]);
  const [phasePhotos, setPhasePhotos] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Comparison state
  const [leftPhase, setLeftPhase] = useState('');
  const [rightPhase, setRightPhase] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [comparisonMode, setComparisonMode] = useState('side-by-side'); // 'side-by-side', 'slider', 'overlay'
  const [sliderPosition, setSliderPosition] = useState(50);
  const [overlayOpacity, setOverlayOpacity] = useState(0.5);

  const sliderRef = useRef(null);
  const isDragging = useRef(false);

  useEffect(() => {
    loadData();
  }, [patientId, caseId, userProfile]);

  const loadData = async () => {
    if (!userProfile) return;
    try {
      setLoading(true);
      const [patientData, caseData, phasesData] = await Promise.all([
        getPatient(userProfile.practiceId, userProfile.id, patientId),
        getCase(userProfile.practiceId, userProfile.id, patientId, caseId),
        getCasePhases(userProfile.practiceId, patientId, caseId)
      ]);

      setPatient(patientData);
      setTreatmentCase(caseData);
      setPhases(phasesData);

      // Load photos for all phases
      const photosMap = {};
      await Promise.all(
        phasesData.map(async (phase) => {
          const photos = await getPhasePhotos(
            userProfile.practiceId, patientId, caseId, phase.id
          );
          photosMap[phase.id] = photos;
        })
      );
      setPhasePhotos(photosMap);

      // Auto-select first and last phases if available
      if (phasesData.length >= 2) {
        setLeftPhase(phasesData[0].id);
        setRightPhase(phasesData[phasesData.length - 1].id);
      } else if (phasesData.length === 1) {
        setLeftPhase(phasesData[0].id);
      }
    } catch (err) {
      console.error('Error loading comparison data:', err);
      setError('Failed to load case data');
    } finally {
      setLoading(false);
    }
  };

  const getPhotoForPhaseCategory = (phaseId, categoryId) => {
    const photos = phasePhotos[phaseId] || [];
    return photos.find(p => p.category === categoryId);
  };

  // Common categories between selected phases
  const getCommonCategories = useCallback(() => {
    if (!leftPhase || !rightPhase) return [];
    const leftPhotos = phasePhotos[leftPhase] || [];
    const rightPhotos = phasePhotos[rightPhase] || [];
    const leftCats = new Set(leftPhotos.map(p => p.category));
    const rightCats = new Set(rightPhotos.map(p => p.category));
    return getAllCategories().filter(c => leftCats.has(c.id) && rightCats.has(c.id));
  }, [leftPhase, rightPhase, phasePhotos]);

  const commonCategories = getCommonCategories();

  // Auto-select first common category
  useEffect(() => {
    if (commonCategories.length > 0 && !selectedCategory) {
      setSelectedCategory(commonCategories[0].id);
    }
  }, [commonCategories, selectedCategory]);

  const leftPhoto = leftPhase && selectedCategory
    ? getPhotoForPhaseCategory(leftPhase, selectedCategory)
    : null;
  const rightPhoto = rightPhase && selectedCategory
    ? getPhotoForPhaseCategory(rightPhase, selectedCategory)
    : null;

  const getPhaseTitle = (phaseId) => {
    const phase = phases.find(p => p.id === phaseId);
    if (!phase) return '';
    const titles = { initial: 'Initial', postOrtho: 'Post-Ortho', provisional: 'Provisional', final: 'Final' };
    return titles[phase.phaseType] || phase.phaseType;
  };

  // Slider drag handlers
  const handleSliderMouseDown = (e) => {
    isDragging.current = true;
    updateSlider(e);
  };

  const handleSliderMouseMove = useCallback((e) => {
    if (!isDragging.current) return;
    updateSlider(e);
  }, []);

  const handleSliderMouseUp = () => {
    isDragging.current = false;
  };

  const updateSlider = (e) => {
    if (!sliderRef.current) return;
    const rect = sliderRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percent = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percent);
  };

  useEffect(() => {
    window.addEventListener('mousemove', handleSliderMouseMove);
    window.addEventListener('mouseup', handleSliderMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleSliderMouseMove);
      window.removeEventListener('mouseup', handleSliderMouseUp);
    };
  }, [handleSliderMouseMove]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center space-x-2 text-sm text-gray-500">
        <Link to={`/patients/${patientId}`} className="hover:text-blue-600">
          {patient ? `${patient.firstName} ${patient.lastName}` : 'Patient'}
        </Link>
        <span>/</span>
        <Link to={`/patients/${patientId}/cases/${caseId}`} className="hover:text-blue-600">
          {treatmentCase?.name || 'Case'}
        </Link>
        <span>/</span>
        <span className="text-gray-900 font-medium">Photo Comparison</span>
      </nav>

      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Before & After Comparison</h1>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Controls */}
      <div className="bg-white shadow rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Left Phase */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Before (Left)</label>
            <select
              value={leftPhase}
              onChange={(e) => { setLeftPhase(e.target.value); setSelectedCategory(''); }}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="">Select phase...</option>
              {phases.map(p => (
                <option key={p.id} value={p.id}>{getPhaseTitle(p.id)}</option>
              ))}
            </select>
          </div>

          {/* Right Phase */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">After (Right)</label>
            <select
              value={rightPhase}
              onChange={(e) => { setRightPhase(e.target.value); setSelectedCategory(''); }}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="">Select phase...</option>
              {phases.map(p => (
                <option key={p.id} value={p.id}>{getPhaseTitle(p.id)}</option>
              ))}
            </select>
          </div>

          {/* Photo Category */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Photo View</label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              disabled={commonCategories.length === 0}
            >
              <option value="">Select photo...</option>
              {commonCategories.map(c => (
                <option key={c.id} value={c.id}>{c.order}. {c.name}</option>
              ))}
            </select>
          </div>

          {/* Comparison Mode */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Mode</label>
            <div className="flex rounded-md shadow-sm">
              {[
                { id: 'side-by-side', label: 'Side-by-Side' },
                { id: 'slider', label: 'Slider' },
                { id: 'overlay', label: 'Overlay' }
              ].map(mode => (
                <button
                  key={mode.id}
                  onClick={() => setComparisonMode(mode.id)}
                  className={`flex-1 px-2 py-2 text-xs font-medium border ${
                    comparisonMode === mode.id
                      ? 'bg-blue-50 text-blue-700 border-blue-300 z-10'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  } ${mode.id === 'side-by-side' ? 'rounded-l-md' : ''} ${mode.id === 'overlay' ? 'rounded-r-md' : ''}`}
                >
                  {mode.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Comparison View */}
      {leftPhoto && rightPhoto ? (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          {/* Side-by-side */}
          {comparisonMode === 'side-by-side' && (
            <div className="grid grid-cols-2 gap-1 bg-gray-200">
              <div className="bg-white">
                <div className="text-center py-2 bg-gray-50 border-b text-sm font-medium text-gray-700">
                  {getPhaseTitle(leftPhase)} - Before
                </div>
                <div className="p-4 flex items-center justify-center min-h-[400px]">
                  <img
                    src={leftPhoto.downloadURL}
                    alt="Before"
                    className="max-h-[60vh] max-w-full object-contain"
                  />
                </div>
              </div>
              <div className="bg-white">
                <div className="text-center py-2 bg-gray-50 border-b text-sm font-medium text-gray-700">
                  {getPhaseTitle(rightPhase)} - After
                </div>
                <div className="p-4 flex items-center justify-center min-h-[400px]">
                  <img
                    src={rightPhoto.downloadURL}
                    alt="After"
                    className="max-h-[60vh] max-w-full object-contain"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Slider */}
          {comparisonMode === 'slider' && (
            <div
              ref={sliderRef}
              className="relative overflow-hidden cursor-col-resize select-none"
              onMouseDown={handleSliderMouseDown}
              style={{ height: '60vh' }}
            >
              {/* After image (full) */}
              <img
                src={rightPhoto.downloadURL}
                alt="After"
                className="absolute inset-0 w-full h-full object-contain"
                draggable={false}
              />

              {/* Before image (clipped) */}
              <div
                className="absolute inset-0 overflow-hidden"
                style={{ width: `${sliderPosition}%` }}
              >
                <img
                  src={leftPhoto.downloadURL}
                  alt="Before"
                  className="absolute inset-0 w-full h-full object-contain"
                  style={{ width: `${100 / (sliderPosition / 100)}%`, maxWidth: 'none' }}
                  draggable={false}
                />
              </div>

              {/* Slider line */}
              <div
                className="absolute top-0 bottom-0 w-0.5 bg-white shadow-lg z-10"
                style={{ left: `${sliderPosition}%` }}
              >
                <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center">
                  <svg className="w-4 h-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
                  </svg>
                </div>
              </div>

              {/* Labels */}
              <div className="absolute top-4 left-4 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-xs">
                Before
              </div>
              <div className="absolute top-4 right-4 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-xs">
                After
              </div>
            </div>
          )}

          {/* Overlay */}
          {comparisonMode === 'overlay' && (
            <div>
              <div className="relative" style={{ height: '60vh' }}>
                {/* Before image */}
                <img
                  src={leftPhoto.downloadURL}
                  alt="Before"
                  className="absolute inset-0 w-full h-full object-contain"
                />
                {/* After image with opacity */}
                <img
                  src={rightPhoto.downloadURL}
                  alt="After"
                  className="absolute inset-0 w-full h-full object-contain"
                  style={{ opacity: overlayOpacity }}
                />
              </div>
              <div className="p-4 bg-gray-50 border-t flex items-center justify-center space-x-4">
                <span className="text-xs text-gray-500 w-16">Before</span>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={overlayOpacity}
                  onChange={(e) => setOverlayOpacity(parseFloat(e.target.value))}
                  className="w-64"
                />
                <span className="text-xs text-gray-500 w-16 text-right">After</span>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg p-12 text-center">
          {!leftPhase || !rightPhase ? (
            <div className="text-gray-500">
              <svg className="mx-auto h-12 w-12 text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className="font-medium">Select two phases to compare</p>
              <p className="text-sm text-gray-400 mt-1">Choose a before and after phase to see side-by-side comparisons</p>
            </div>
          ) : commonCategories.length === 0 ? (
            <div className="text-gray-500">
              <p className="font-medium">No matching photos found</p>
              <p className="text-sm text-gray-400 mt-1">The selected phases don't have photos in the same categories</p>
            </div>
          ) : (
            <div className="text-gray-500">
              <p className="font-medium">Select a photo category to compare</p>
            </div>
          )}
        </div>
      )}

      {/* Category quick select */}
      {commonCategories.length > 0 && (
        <div className="bg-white shadow rounded-lg p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Available Comparisons</h3>
          <div className="flex flex-wrap gap-2">
            {commonCategories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                  selectedCategory === cat.id
                    ? 'bg-blue-100 text-blue-700 border-blue-300'
                    : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotoComparison;
